import { yupResolver } from '@hookform/resolvers/yup';
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Typography,
} from '@mui/material';
import { useState } from 'react';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import Close from '@mui/icons-material/Close';
import CreateOutlinedIcon from '@mui/icons-material/CreateOutlined';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';
import { StudyCourseType } from '@/api/enum/common.enum';
import { StudyCourseSetStatus } from '@/api/DTO/studyCourse/studyCourseList.interface';
import { useSetStudyCourseStatusMutation } from '@/api/studyCourse/studyCourseListApiSlice';
import ConfirmationDialog from '@/common/components/ConfirmationDialog/ConfirmationDialog';
import useModal from '@/common/components/ConfirmationDialog/useModal';

interface SettingStatusModalProps {
	currentStatus: string | number | undefined;
	setStatusButton?: boolean;
	selectedId: string[];
	setSelectedId?: any;
	studyCourseStatusList: { id: string | number; name: string }[];
}

const SettingStatusModal = (props: SettingStatusModalProps) => {
	console.log();
	const {
		selectedId,
		setSelectedId,
		studyCourseStatusList,
		setStatusButton,
		currentStatus,
	} = props;
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [setStudyCourseStatus] = useSetStudyCourseStatusMutation();
	const showModal = useModal();

	// const { userRoles } = useGetUser<useGetUserResultType>({
	// 	roleObj: {
	// 		statusRemark: ['statusRemark', 'communityResources'],
	// 	},
	// });

	const defaultValues: DefaultValues<StudyCourseSetStatus> = {
		mark: '',
		status: '',
	};

	const formSchema = Yup.object().shape({
		status: Yup.string().required(t('errorMessage.pleaseSelect')),
	});

	const methods = useForm<StudyCourseSetStatus>({
		defaultValues,
		resolver: yupResolver(formSchema),
	});

	const handleClose = () => {
		methods.reset();
		setOpen(false);
	};

	const onSubmit: SubmitHandler<StudyCourseSetStatus> = async (data) => {
		if (
			(currentStatus == StudyCourseType.released ||
				currentStatus == StudyCourseType.removed) &&
			(Number(data.status) == StudyCourseType.draft ||
				Number(data.status) == StudyCourseType.directorApproval ||
				Number(data.status) == StudyCourseType.supervisorApproval ||
				Number(data.status) == StudyCourseType.awaitingRelease)
		) {
			showModal((props: any) => (
				<ConfirmationDialog
					{...props}
					showCloseIcon={true}
					title={t('common.editStatus')}
					titleProps={{ variant: 'h6' }}
					description={
						<>
							<Typography variant='body1'>
								{t('studyCourse.willBeHappen')}
							</Typography>
							<br />
							<Typography variant='body1'>
								{t('studyCourse.willBeHappen1')}
							</Typography>
							<Typography variant='body1'>
								{t('studyCourse.willBeHappen2')}
							</Typography>
							<br />
							<Typography variant='body1'>
								{t('studyCourse.isEditStatus')}
							</Typography>
						</>
					}
					cancellationText={t('button.cancel')}
					confirmationText={t('button.sureEdit')}
					confirmationButtonProps={{ type: 'submit', variant: 'contained' }}
					onConfirm={async () => {
						onSubmitApi(data);
					}}
				/>
			));
		} else {
			onSubmitApi(data);
		}
	};
	const onSubmitApi = async (data: StudyCourseSetStatus) => {
		const newData: any = {
			ids: selectedId,
			mark: data.mark,
			status: data.status,
		};
		const tipTitle =
			Number(data.status) == StudyCourseType.draft
				? t('snackbar.studyCourseStatusToDraft')
				: Number(data.status) == StudyCourseType.directorApproval
				? t('snackbar.studyCourseStatusToDirectorApproval')
				: Number(data.status) == StudyCourseType.supervisorApproval
				? t('snackbar.studyCourseStatusToSupervisorApproval')
				: Number(data.status) == StudyCourseType.awaitingRelease
				? t('snackbar.studyCourseStatusToAwaitingRelease')
				: Number(data.status) == StudyCourseType.released
				? t('snackbar.studyCourseStatusToReleased')
				: Number(data.status) == StudyCourseType.removed
				? t('snackbar.studyCourseStatusToRemoved')
				: '';
		const res = await setStudyCourseStatus(newData);
		if ('data' in res) {
			enqueueSnackbar(tipTitle, {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
			!setStatusButton && setSelectedId([]);
		}
	};

	return (
		<>
			{setStatusButton ? (
				<Button
					startIcon={<CreateOutlinedIcon />}
					onClick={() => setOpen(true)}
				/>
			) : (
				<Button
					variant='outlined'
					startIcon={<CreateOutlinedIcon />}
					disabled={selectedId.length === 0}
					onClick={() => setOpen(true)}
				>
					{t('button.settingStatus')}
					{selectedId.length > 0 && ` (${selectedId.length})`}
				</Button>
			)}

			<Dialog
				open={open}
				onClose={handleClose}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='xs'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('common.settingStatus')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={handleClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<Close />
				</IconButton>
				<DialogContent>
					<FormProvider {...methods}>
						<form onSubmit={methods.handleSubmit(onSubmit)}>
							<Grid container spacing={2} marginTop={0}>
								<Grid item xs={12}>
									<AppDropDownMenu
										disabledItem={currentStatus}
										control={methods.control}
										name='status'
										label={t('common.status')}
										options={studyCourseStatusList}
										required
									/>
								</Grid>
								{/* {userRoles.statusRemark && ( */}
								<Grid item xs={12}>
									<AppTextField
										control={methods.control}
										name='mark'
										label={t('common.remark')}
									/>
								</Grid>
								{/* )} */}
							</Grid>
						</form>
					</FormProvider>
				</DialogContent>
				<DialogActions>
					<Button
						type='submit'
						variant='contained'
						onClick={methods.handleSubmit(onSubmit)}
					>
						{t('button.confirm')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default SettingStatusModal;
